import { useRef, useState } from 'react';

import {
  LegalClientBodyEntity,
  LegalClientBodyType,
} from 'api/types/entity/legal/legalClientBody';
import { ConfirmationDialog } from 'components';
import { useTranslation } from 'libs/i18n';
import { Button, Stack } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { Icon } from 'libs/ui/Icon';

import { DATAX_ADD_ITEM } from '../../../../common/constants';
import {
  useFormContext,
  Field,
  MAX_AUTHORITIES,
  CompanyStructureFormType,
} from '../../../forms/companyStructureForm';

import { BodyItem } from './BodyItem';

export interface FormContentProps {
  bodies: LegalClientBodyEntity[];
  onAdd: (v: CompanyStructureFormType) => Promise<any>;
  onEdit: (v: NonNullable<CompanyStructureFormType>) => Promise<any>;
  onDelete: (id: string) => Promise<any>;
}

export const FormContent = ({
  bodies,
  onAdd,
  onEdit,
  onDelete,
}: FormContentProps) => {
  const [loading, setLoading] = useState(false);

  const editItemIndex = useRef<number>();

  const { values, setValues, validateField, setErrors } = useFormContext();

  const { t } = useTranslation();

  const { isOpen, onClose, open } = useDialog();

  const handleAdd = () => {
    editItemIndex.current = undefined;
    setValues({} as CompanyStructureFormType);
    open();
  };

  const handleEdit = (body: LegalClientBodyEntity) => {
    setValues({
      type: {
        label: t(`onboarding.companyStructure.type.types.${body.type}`),
        value: body.type,
      },
      name: body.name,
      id: body.id,
      otherType: body.otherType,
    });
    open();
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setValues({ type: {} } as CompanyStructureFormType);
      setErrors({});
    }, 100);
  };

  const onConfirm = async () => {
    const isLabelValid = await validateField('type');
    const isNamelValid = await validateField('name');
    const isOtherTypeValid = await validateField('otherType');

    if (isLabelValid && isNamelValid && isOtherTypeValid) {
      setLoading(true);
      try {
        if (values.id) {
          await onEdit(values).catch();
        } else {
          await onAdd(values).catch();
        }
        setLoading(false);
        handleClose();
      } catch (error) {
        setLoading(false);
      }
    }
  };

  return (
    <Stack spacing={32}>
      {bodies.map((value) => (
        <BodyItem
          key={value.id}
          body={value}
          onDelete={onDelete}
          onEdit={handleEdit}
        />
      ))}

      <Button
        fullWidth
        datax={DATAX_ADD_ITEM}
        disabled={bodies.length >= MAX_AUTHORITIES}
        label={t('onboarding.companyStructure.addButton')}
        mode="medium"
        startIcon={<Icon.Plus />}
        onClick={handleAdd}
      />

      <ConfirmationDialog
        fullWidth
        headerCentered
        confirmButtonLabel={t('common.save')}
        isOpen={isOpen}
        loading={loading}
        title={t('onboarding.companyStructure.dialogTitle')}
        onBack={handleClose}
        onClose={handleClose}
        onConfirm={onConfirm}
      >
        <Stack spacing={24}>
          <Field.Autocomplete
            label={t('onboarding.companyStructure.type.label')}
            name="type"
            optionLabelKey="label"
            options={Object.values(LegalClientBodyType).map((type) => ({
              label: t(`onboarding.companyStructure.type.types.${type}`),
              value: type,
            }))}
            optionValueKey="value"
            placeholder={t('onboarding.companyStructure.type.label')}
            variant="select"
          />
          {values.type?.value === LegalClientBodyType.Other && (
            <Field.Text
              fullWidth
              helperTextAbsolute
              label={t('onboarding.companyStructure.otherType.label')}
              name="otherType"
              placeholder={t(
                'onboarding.companyStructure.otherType.placeholder'
              )}
            />
          )}
          <Field.Text
            fullWidth
            helperTextAbsolute
            label={t('onboarding.companyStructure.name.label')}
            name="name"
            placeholder={t('onboarding.companyStructure.name.placeholder')}
          />
        </Stack>
      </ConfirmationDialog>
    </Stack>
  );
};
